//Author Julie Ho
//Date August 18th, 2025
import {getReducer, getSetStateFunction, getAPICallGenerator, postAPICallGenerator, putAPICallGenerator, deleteAPICallGenerator, callBackGenerator, showMessage} from '../../util/util';
import React, {useReducer, useEffect} from 'react';
import { Button } from 'reactstrap';
import './appraisal.css';
import { useParams } from 'react-router-dom';

//initialize the state
const initialState = {
  id: '',
  email: '',
  appraiserName: '',
  singleFamily: '',
  condo: '',
  multiFamily: '',
  manufactured: '',
  landAppraisal: '',
  desktopAppraisal: '',
  fhaAddition: '',
  usdaAddition: '',
  rentalFormAddition: '',
  operatingIncomeAddition: '',
  pdr: '',
  appraisalUpdate: '',
  finalInspection: '',
  disasterInspection: '',
  condoDriveBy: '',
  SFRDriveBy: '',
  exteriorOnly: '',
  deskReview: '',
  fieldReview: '',
  rushFee: '',
  tripFee: '',
  rentSurvey: '',
  operatingIncomeStatement: '',
  coverageRadius: '',
  orderCapacity: '',
  totalFees: [],

  termsAgreed: false,
  appraiserStatus: null,
  termAgreement: 'no',
  showTerms: false,

  coverageCounty: '',
  coverageCounties:[],

  nonCoverageCounty:'',
  nonCoverageCounties:[],
};

//reducer function that perform state update
const reducer = getReducer();

const AppraiserFee = (props) => {
  const controller = new AbortController();

  const [state, dispatch] = useReducer(reducer, initialState);
  const { id, email } = useParams();

  const { appraiserID, appraiserEmail, isModal = false } = props;


  const isAdminMode = false;

  //wrapper function
  const setState = getSetStateFunction(dispatch);

  const httpPost = postAPICallGenerator(props, {signal: controller.signal, noToken: true});
  const httpGet = getAPICallGenerator(props, {signal: controller.signal, noToken: true});
  const httpDelete = deleteAPICallGenerator(props, {signal: controller.signal, noToken: true});

  useEffect(() => {
      const finalId = appraiserID || id || '';
      const finalEmail = appraiserEmail || email || '';

      if(finalId && finalEmail) {
        loadFeeData();
      }
      
      return () => controller.abort();
    }, [appraiserID, appraiserEmail, id, email]
  );

  const handleInputChange = (field, value) => {
    if (field === 'id') {
      setState({ [field]: value });
      return;
    }

    // Allow empty value or valid number format
    if (value === '' || /^[\d.]*$/.test(value)) {
      setState({ [field]: value });
    }
  };

  const validateForm = () => {
    const requiredFields = [
      { field: 'singleFamily', name: 'Single Family' },
      { field: 'condo', name: 'Condo' },
      { field: 'multiFamily', name: 'Multi Family' },
      { field: 'fhaAddition', name: 'FHA Addition' },
      { field: 'rentalFormAddition', name: 'Rental Form Addition' },
      { field: 'operatingIncomeAddition', name: 'Operating Income Addition' },
    ];

    const optionalFields = [
      { field: 'pdr', name: 'Property Dataset Report' },
      { field: 'appraisalUpdate', name: 'Appraisal Update' },
      { field: 'finalInspection', name: 'Final Inspection' },
      { field: 'disasterInspection', name: 'Disaster Inspection' },
      { field: 'condoDriveBy', name: 'Condo Drive By' },
      { field: 'SFRDriveBy', name: 'SFR Drive By' },
      { field: 'exteriorOnly', name: 'Exterior Only' },
      { field: 'deskReview', name: 'Desk Review' },
      { field: 'fieldReview', name: 'Field Review' },
      { field: 'rushFee', name: 'Rush Fee' },
      { field: 'tripFee', name: 'Trip Fee' },
      { field: 'rentSurvey', name: 'Rent Survey' },
      { field: 'operatingIncomeStatement', name: 'Operating Income Statement' },
      { field: 'coverageRadius', name: 'Coverage Radius' },
      { field: 'orderCapacity', name: 'Order Capacity' }
    ];

    
      for (let i = 0; i < requiredFields.length; i++) {
        const { field, name } = requiredFields[i];
        const value = state[field];

        //required fields
        if (!value || value.trim() === '') {
          showMessage('error', `${name} is required`);
          return false;
        }

        //validate number input
        const num = parseFloat(value);
        if (isNaN(num) || num < 0) {
          showMessage('error', `${name} must be a valid positive number`);
          return false;
        }
      }
      
      if (state.showTerms && !state.termsAgreed) {
        showMessage('error', 'Please agree to the Terms of Agreement');
        return false;
      }

    for (let i = 0; i < optionalFields.length; i++) {
      const { field, name } = optionalFields[i];
      const value = state[field];
      
      if (value && value.trim() !== '') {
        const num = parseFloat(value);
        if (isNaN(num) || num < 0) {
          showMessage('error', `${name} must be a valid positive number if provided`);
          return false;
        }
      }
    }

    return true;
  };

  const renderLabel = (text, isRequired = false) => {
    return (
      <label className="form-label">
        {isRequired && <span className="required-star">*</span>}
        {text}
      </label>
    );
  };


  const addCoverageCounty = () => {
    const countyName = state.coverageCounty.trim();
    if(!countyName) {
      showMessage('error', 'Please enter a county name');
      return;
    }

    if(state.coverageCounties.some(c=>c.toLowerCase() === countyName.toLowerCase())) {
      showMessage('error', 'This county has already been added to covered list.');
      return;
    }

    setState({coverageCounties:[...state.coverageCounties, countyName], coverageCounty:''});
    showMessage('success', 'County added to covered list');
  };

  const removeCoverageCounty = (countyName) => {
    setState({coverageCounties:state.coverageCounties.filter(c=>c!==countyName)});
    showMessage('success', 'County removed from covered list');
  };


  const addNonCoverageCounty = () => {
    const countyName = state.nonCoverageCounty.trim();
    if(!countyName) {
      showMessage('error', 'Please enter a county name');
      return;
    }

    if(state.nonCoverageCounties.some(c=>c.toLowerCase() === countyName.toLowerCase())) {
      showMessage('error', 'This county has already been added to non-covered list.');
      return;
    }

    setState({nonCoverageCounties:[...state.nonCoverageCounties, countyName], nonCoverageCounty:''});
    showMessage('success', 'County added to non-covered list');
  };

  const removeNonCoverageCounty = (countyName) => {
    setState({nonCoverageCounties:state.nonCoverageCounties.filter(c=>c!==countyName)});
    showMessage('success', 'County removed from non-covered list');
  };

  //API call
  const loadFeeData = () => {
    const finalId = appraiserID || id || '';
    const finalEmail = appraiserEmail || email || '';
    
    if (!finalId || !finalEmail) {
      return;
    }

    const callback = (response) => {
      if (response && response.data && response.data.code === '00') {
        const data = response.data.data;

        const coverageCounties = (data.coverageCounties || []).map(c => c.countyName);
        const nonCoverageCounties = (data.nonCoverageCounties || []).map(c => c.countyName);
        
        setState({
          id: data.id || '',
          email: data.email || '',
          appraiserName: data.appraiserName || '',
          singleFamily: data.singleFamily || '',
          condo: data.condo || '',
          multiFamily: data.multiFamily || '',
          manufactured: data.manufactured || '',
          landAppraisal: data.landAppraisal || '',
          desktopAppraisal: data.desktopAppraisal || '',
          fhaAddition: data.fhaAddition || '',
          usdaAddition: data.usdaAddition || '',
          rentalFormAddition: data.rentalFormAddition || '',
          operatingIncomeAddition: data.operatingIncomeAddition || '',
          pdr: data.pdr || '',
          appraisalUpdate: data.appraisalUpdate || '',
          finalInspection: data.finalInspection || '',
          disasterInspection: data.disasterInspection || '',
          condoDriveBy: data.condoDriveBy || '',
          SFRDriveBy: data.SFRDriveBy || '',
          exteriorOnly: data.exteriorOnly || '',
          deskReview: data.deskReview || '',
          fieldReview: data.fieldReview || '',
          rushFee: data.rushFee || '',
          tripFee: data.tripFee || '',
          rentSurvey: data.rentSurvey || '',
          operatingIncomeStatement: data.operatingIncomeStatement || '',
          coverageRadius: data.coverageRadius || '',
          orderCapacity: data.orderCapacity || '',
          appraiserStatus: data.appraiserStatus || null,
          termAgreement: data.termAgreement || 'no',
          showTerms: data.showTerms || false,
          termsAgreed: data.termAgreement === 'yes',
          coverageCounties: coverageCounties,
          nonCoverageCounties: nonCoverageCounties
        });
      } else {
        setState({id: finalId, email: finalEmail });
      }
    };

    httpGet(`appraiser/fee/get/setup/${finalId}/${finalEmail}`, '', 'Failed to load fee data', callback);
  };


  const totalfeeCalculation = () => {
    let updatedCoverageCounties = [...state.coverageCounties];
    let updatedNonCoverageCounties = [...state.nonCoverageCounties];

    //CHECK IF INPUT AREA IS NON-EMPTY WHEN USER ENROLL
    const coverageCountyInput = state.coverageCounty.trim();
    if (coverageCountyInput && !updatedCoverageCounties.some(c => c.toLowerCase() === coverageCountyInput.toLowerCase())) {
      updatedCoverageCounties.push(coverageCountyInput);
    }

    const nonCoverageCountyInput = state.nonCoverageCounty.trim();
    if (nonCoverageCountyInput && !updatedNonCoverageCounties.some(c => c.toLowerCase() === nonCoverageCountyInput.toLowerCase())) {
      updatedNonCoverageCounties.push(nonCoverageCountyInput);
    }

    if (coverageCountyInput || nonCoverageCountyInput) {
      setState({
        coverageCounties: updatedCoverageCounties,
        nonCoverageCounties: updatedNonCoverageCounties,
        coverageCounty: '',
        nonCoverageCounty: '' 
      });
    }

    if (!validateForm()) {
      return;
    }

    let parameters = [
      { field: 'ID', value: state.id },
      { field: 'email', value: state.email },
      { field: 'isAdmin', value: isAdminMode },
      { field: 'termsAgreed', value: state.termsAgreed },
      { field: 'singleFamily', value: state.singleFamily ? parseFloat(state.singleFamily) : 0 },
      { field: 'condo', value: state.condo ? parseFloat(state.condo) : 0 },
      { field: 'multiFamily', value: state.multiFamily ? parseFloat(state.multiFamily) : 0 },
      { field: 'manufactured', value: state.manufactured ? parseFloat(state.manufactured) : 0 },
      { field: 'landAppraisal', value: state.landAppraisal ? parseFloat(state.landAppraisal) : 0 },
      { field: 'desktopAppraisal', value: state.desktopAppraisal ? parseFloat(state.desktopAppraisal) : 0 },
      { field: 'fhaAddition', value: state.fhaAddition ? parseFloat(state.fhaAddition) : 0 },
      { field: 'usdaAddition', value: state.usdaAddition ? parseFloat(state.usdaAddition) : 0 },
      { field: 'rentalFormAddition', value: state.rentalFormAddition ? parseFloat(state.rentalFormAddition) : 0 },
      { field: 'operatingIncomeAddition', value: state.operatingIncomeAddition ? parseFloat(state.operatingIncomeAddition) : 0 },
      { field: 'pdr', value: state.pdr && state.pdr.trim() !== '' ? parseFloat(state.pdr) : 0 },
      { field: 'appraisalUpdate', value: state.appraisalUpdate && state.appraisalUpdate.trim() !== '' ? parseFloat(state.appraisalUpdate) : 0 },
      { field: 'finalInspection', value: state.finalInspection && state.finalInspection.trim() !== '' ? parseFloat(state.finalInspection) : 0 },
      { field: 'disasterInspection', value: state.disasterInspection && state.disasterInspection.trim() !== '' ? parseFloat(state.disasterInspection) : 0 },
      { field: 'condoDriveBy', value: state.condoDriveBy && state.condoDriveBy.trim() !== '' ? parseFloat(state.condoDriveBy) : 0 },
      { field: 'SFRDriveBy', value: state.SFRDriveBy && state.SFRDriveBy.trim() !== '' ? parseFloat(state.SFRDriveBy) : 0 },
      { field: 'exteriorOnly', value: state.exteriorOnly && state.exteriorOnly.trim() !== '' ? parseFloat(state.exteriorOnly) : 0 },
      { field: 'deskReview', value: state.deskReview && state.deskReview.trim() !== '' ? parseFloat(state.deskReview) : 0 },
      { field: 'fieldReview', value: state.fieldReview && state.fieldReview.trim() !== '' ? parseFloat(state.fieldReview) : 0 },
      { field: 'rushFee', value: state.rushFee && state.rushFee.trim() !== '' ? parseFloat(state.rushFee) : 0 },
      { field: 'tripFee', value: state.tripFee && state.tripFee.trim() !== '' ? parseFloat(state.tripFee) : 0 },
      { field: 'rentSurvey', value: state.rentSurvey && state.rentSurvey.trim() !== '' ? parseFloat(state.rentSurvey) : 0 },
      { field: 'operatingIncomeStatement', value: state.operatingIncomeStatement && state.operatingIncomeStatement.trim() !== '' ? parseFloat(state.operatingIncomeStatement) : 0 },
      { field: 'coverageRadius', value: state.coverageRadius && state.coverageRadius.trim() !== '' ? parseFloat(state.coverageRadius) : 0 },
      { field: 'orderCapacity', value: state.orderCapacity && state.orderCapacity.trim() !== '' ? parseFloat(state.orderCapacity) : 0 },
      { field: 'coverageCounties', value: updatedCoverageCounties },
      { field: 'nonCoverageCounties', value: updatedNonCoverageCounties }
    ];

    if(updatedCoverageCounties.length<=0){
      showMessage('error', 'Please at least include one county that you cover.');
      return;
    }
    if(state.coverageRadius<=0){
      showMessage('error', 'Please tell us your coverage radius from your home office.');
      return;
    }
    //fee/setup endpoint call
    let setupCallback = (response) => {
      if (response && response.data.code === '00') {
        // fee/AllInOne endpoint call
        let allInOneCallback = (response2) => {
          if (response2 && response2.data.code === '00') {
            setState({ totalFees: response2.data || [] });
            showMessage('success', 'Fee calculation completed. Your appraiser fee schedule has been successfully updated.');

            loadFeeData();
          } else {
            showMessage('error', 'Failed to calculate combination fees.');
          }
        };

        httpPost('appraiser/fee/AllInOne', parameters, '', 'Failed to calculate combination fees', allInOneCallback);
      } else {
        showMessage('error', 'Failed to save basic fees. Please try again.');
      }
    };

    httpPost('appraiser/fee/setup', parameters, '', 'Failed to save fees', setupCallback);
  };

  //render
  return (
    <div className="appraiser-fee-container">

      <div style={{
        backgroundColor: '#fff3cd',
        border: '3px solid orange',
        padding: '10px',
        marginBottom: '20px',
        fontSize: '16px'
      }}>
        <h5 style={{margin: 0, marginBottom: '10px'}}>
          👤 Appraiser - {state.appraiserName}
        </h5>
        <div>
          <div><strong>Email:</strong> {appraiserEmail || email || 'undefined'}</div>
        </div>
      </div>
      
      <h3 className="appraiser-fee-title" style={{ color: '#FF0000', marginBottom: '25px' }}>
        Enrolling Auto Assign
      </h3>

      <legend className="fieldset-legend">Appraiser Service Area and Fee Schedule</legend>
      <form onSubmit={(e) => e.preventDefault()}>
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Coverage Area</legend>
          
            {/*Covered Counties*/}
            {!isAdminMode && (
              <div style={{ marginTop: '20px' }}>
                <div style={{color: '#e8172cff', fontSize: '13px', marginTop: '5px', marginBottom: '0px'}}>
                  <span style={{ fontWeight: 'bold' }}>* </span>
                    Please enter only the county name without the word "County" (e.g., "Orange" not "Orange County")
                </div>

                <label className="form-label">
                  <span className="required-star" style={{ marginLeft: '5px' }}>*</span>Coverage County
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', maxWidth: '62.5%' }}>
                  <input
                    type="text"
                    value={state.coverageCounty}
                    onChange={(e) => setState({ coverageCounty: e.target.value })}
                    className="form-input"
                    placeholder="Enter covered county name"
                    style={{ flex: 1 }}
                  />
                  <button onClick={addCoverageCounty}
                    style={{background: '#f0f0f0', border: '1px solid #d0d0d0', outline: 'none', cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#28a745', borderRadius: '4px'}}
                    title="Add county"
                  >
                    <i className="fa-jelly fa-regular fa-plus"></i>
                  </button>
                </div>
                
                {state.coverageCounties && state.coverageCounties.length > 0 && (
                  <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {state.coverageCounties.map((countyName, index) => (
                      <div 
                        key={index}
                        style={{display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '2px 2px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '3px', fontSize: '13px'}}
                      >
                        <button
                          onClick={() => removeCoverageCounty(countyName)}
                          style={{background: 'transparent', color: '#dc3545',border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold', padding: 0, lineHeight: 1, flexShrink: 0, width: 'auto', height: 'auto'}}
                          title="Remove county"
                        >
                          -
                        </button>
                        <span>{countyName}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          <div style={{color: '#e8172cff', fontSize: '13px', marginTop: '5px', marginBottom: '0px'}}>
            <span style={{ fontWeight: 'bold' }}>* </span>
            This is the maximum distance you can service from your home base or mailing address to the subject property
          </div>
          <div className="form-grid">
            <div>
              
              <label className="form-label">
                <span className="required-star" style={{ marginLeft: '5px' }}>*</span>Coverage Radius (miles)
              </label>
              <input
                type="text"
                required={true}
                value={state.coverageRadius}
                onChange={(e) => handleInputChange('coverageRadius', e.target.value)}
                className="form-input"
                placeholder="0.00"
              />
            </div>

             {isAdminMode && (
              <div>
                <label className="form-label">
                  Maximum orders
                </label>
                <input
                  type="text"
                  value={state.orderCapacity}
                  onChange={(e) => handleInputChange('orderCapacity', e.target.value)}
                  className="form-input"
                  placeholder="0.00"
                />
              </div>
            )}
          </div>


           
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Base Fees for Non-Complex Properties</legend>
          
          <div className="form-grid">
            <div>
              {renderLabel('Single Family (1004)', true)}
              <input
                type="text"
                value={state.singleFamily}
                onChange={(e) => handleInputChange('singleFamily', e.target.value)}
                className="form-input"
                placeholder="0.00"
              />
            </div>

            <div>
              {renderLabel('Condo (1073)', true)}
              <input
                type="text"
                value={state.condo}
                onChange={(e) => handleInputChange('condo', e.target.value)}
                className="form-input"
                placeholder="0.00"
              />
            </div>

            <div>
              {renderLabel('Multi Family (1025)', true)}
              <input
                type="text"
                value={state.multiFamily}
                onChange={(e) => handleInputChange('multiFamily', e.target.value)}
                className="form-input"
                placeholder="0.00"
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Additional Fees</legend>
          
          <div className="form-grid">
            <div>
              {renderLabel('FHA Addition', true)}
              <input
                type="text"
                value={state.fhaAddition}
                onChange={(e) => handleInputChange('fhaAddition', e.target.value)}
                className="form-input"
                placeholder="0.00"
              />
            </div>

            <div>
              {renderLabel('Rental Form Addition (1007)', true)}
              <input
                type="text"
                value={state.rentalFormAddition}
                onChange={(e) => handleInputChange('rentalFormAddition', e.target.value)}
                className="form-input"
                placeholder="0.00"
              />
            </div>

            <div>
              {renderLabel('Operating Income Addition (216)', true)}
              <input
                type="text"
                value={state.operatingIncomeAddition}
                onChange={(e) => handleInputChange('operatingIncomeAddition', e.target.value)}
                className="form-input"
                placeholder="0.00"
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Stand Alone Fees</legend>
          
          <div className="form-grid">
            
            <div>
              {renderLabel('Final Inspection with Photos (1004D)', false)}
              <input
                type="text"
                value={state.finalInspection}
                onChange={(e) => handleInputChange('finalInspection', e.target.value)}
                className="form-input"
                placeholder="0.00"
              />
            </div>
          </div>
        </fieldset> 

        

        {state.showTerms && (
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Auto-Assignment Terms of Engagement:</legend>

            <ul style={{ textAlign: 'left', paddingLeft: '20px' }}>
              <li>
                Appraiser has 1 hour and 30 minutes after receipt of notification to refuse an assignment. If no refusal of assignment is received, the order will be placed in "Pending Inspection."
              </li>
              <li>
                Turn around times and fees will be prearranged for auto-assigned orders and no bidding will occur.
              </li>
              <li>
                Auto-assignment will operate on a round-robin basis if more than one appraiser is eligible in any given market.
              </li>
            </ul>
            
            <div style={{ padding: '15px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '10px', 
                cursor: state.termAgreement === 'yes' ? 'not-allowed' : 'pointer',
                opacity: state.termAgreement === 'yes' ? 0.7 : 1
              }}>
                <input
                  type="checkbox"
                  checked={state.termAgreement === 'yes' ? true : state.termsAgreed}
                  onChange={(e) => {
                    if (state.termAgreement !== 'yes') {
                      setState({ termsAgreed: e.target.checked });
                    }
                  }}
                  style={{ marginTop: '4px', cursor: state.termAgreement === 'yes' ? 'not-allowed' : 'pointer' }}
                  disabled={state.termAgreement === 'yes'}
                />
                <span>
                  I, <strong>{state.appraiserName || '(appraiser\'s name)'}</strong>, acknowledge that I have agreed to receive automatically assigned appraisal assignments from @Home VMS and understand the terms of engagement. I also acknowledge that I may be removed from the auto-assignment eligibility list at any time at the discretion of @Home VMS.
                  {state.termAgreement !== 'yes' && (
                    <span className="required-star" style={{ marginLeft: '5px' }}>*</span>
                  )}
                </span>
              </label>
            </div>
          </fieldset>
        )}

        <div className="button-container">
          <Button color="warning" onClick={totalfeeCalculation}>
            Enroll
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AppraiserFee;